import { useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Loader2, Upload, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logActivity } from "@/lib/activityLog";
import {
  AccreditationRequest,
  AccreditationTerm,
  RequestStatus,
  TERM_DESCRIPTIONS,
  TERM_LABELS,
} from "./types";

const DOC_LABELS: Record<string, string> = {
  national_id: "National ID",
  press_card: "Press Card",
  assignment_letter: "Assignment Letter",
  photograph: "Photograph",
};

const STATUS_OPTIONS: { value: RequestStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "docs_requested", label: "Documents Requested" },
  { value: "under_review", label: "Under Review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

interface RequestReviewSheetProps {
  request: AccreditationRequest | null;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
  onApproveAndIssuePass?: (journalistId: string) => void;
}

export function RequestReviewSheet({
  request,
  open,
  onClose,
  onUpdated,
  onApproveAndIssuePass,
}: RequestReviewSheetProps) {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const [status, setStatus] = useState<RequestStatus>(
    request?.status ?? "pending"
  );
  const [reviewerNotes, setReviewerNotes] = useState(
    request?.reviewer_notes ?? ""
  );
  const [rejectionReason, setRejectionReason] = useState(
    request?.rejection_reason ?? ""
  );
  const [docFlags, setDocFlags] = useState<Record<string, boolean>>(
    (request?.document_flags as Record<string, boolean>) ?? {}
  );
  const [docUrls, setDocUrls] = useState<Record<string, string>>(
    ((request as any)?.document_urls as Record<string, string>) ?? {}
  );
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [term, setTerm] = useState<AccreditationTerm>(
    request?.accreditation_term ?? "medium_term"
  );

  // Reset local state when request changes
  if (request && request.status !== status && !submitting) {
    setStatus(request.status);
    setReviewerNotes(request.reviewer_notes ?? "");
    setRejectionReason(request.rejection_reason ?? "");
    setDocFlags((request.document_flags as Record<string, boolean>) ?? {});
    setDocUrls(((request as any).document_urls as Record<string, string>) ?? {});
    setTerm(request.accreditation_term ?? "medium_term");
  }

  async function handleFileUpload(key: string, file: File) {
    if (!request) return;
    setUploadingDoc(key);
    try {
      const ext = file.name.split(".").pop();
      const path = `${request.journalist_id}/${key}-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("journalist-docs")
        .upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;

      const nextFlags = { ...docFlags, [key]: true };
      const nextUrls = { ...docUrls, [key]: path };
      const { error } = await supabase
        .from("accreditation_requests" as any)
        .update({ document_flags: nextFlags, document_urls: nextUrls })
        .eq("id", request.id);
      if (error) throw error;

      setDocFlags(nextFlags);
      setDocUrls(nextUrls);
      toast({ title: "Document uploaded" });
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally {
      setUploadingDoc(null);
    }
  }

  async function viewDocument(path: string) {
    const { data, error } = await supabase.storage
      .from("journalist-docs")
      .createSignedUrl(path, 3600);
    if (error || !data?.signedUrl) {
      toast({ title: "Could not open document", description: error?.message, variant: "destructive" });
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  }

  async function save(overrideStatus?: RequestStatus) {
    if (!request) return;
    const finalStatus = overrideStatus ?? status;
    if (finalStatus === "rejected" && !rejectionReason.trim()) {
      toast({
        title: "Rejection reason required",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("accreditation_requests" as any)
        .update({
          status: finalStatus,
          accreditation_term: term,
          document_flags: docFlags,
          reviewer_notes: reviewerNotes || null,
          rejection_reason: finalStatus === "rejected" ? rejectionReason : null,
          reviewed_by: user?.id ?? null,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", request.id);
      if (error) throw error;
      toast({ title: "Request updated" });

      if (finalStatus === "approved") {
        const journalist = request.journalist;
        await logActivity({
          orgId: request.org_id,
          actionType: "accreditation_approved",
          description: `Accreditation approved for ${journalist ? `${journalist.first_name} ${journalist.last_name}` : "a journalist"}`,
          metadata: { journalist_id: request.journalist_id, request_id: request.id },
        });
      }

      // Fire-and-forget accreditation email for approve/reject
      if (finalStatus === "approved" || finalStatus === "rejected") {
        void (async () => {
          try {
            // Fetch org name and admin profile in parallel
            const [orgResult, profileResult] = await Promise.all([
              supabase.from("organizations").select("name").eq("id", request.org_id).single(),
              user
                ? supabase.from("profiles").select("full_name").eq("user_id", user.id).single()
                : Promise.resolve({ data: null }),
            ])
            const orgName = (orgResult.data as any)?.name ?? ""
            const adminName = (profileResult.data as any)?.full_name ?? ""
            const journalist = request.journalist
            await supabase.functions.invoke("send-accreditation-email", {
              body: {
                journalist_user_id: request.journalist_id,
                request_id: request.id,
                status: finalStatus,
                org_name: orgName,
                admin_name: adminName,
                journalist_first_name: journalist?.first_name ?? "",
                journalist_last_name: journalist?.last_name ?? "",
                journalist_email: journalist?.email ?? "",
                ...(finalStatus === "rejected" ? { feedback: rejectionReason } : {}),
              },
            })
          } catch {
            // Silently ignore email errors — never block the UI
          }
        })()
      }

      if (finalStatus === "approved" && onApproveAndIssuePass) {
        const shouldIssue = window.confirm(
          "Request approved. Would you like to issue a press pass for this journalist now?"
        );
        if (shouldIssue) {
          onApproveAndIssuePass(request.journalist_id);
        }
      }

      onUpdated();
      onClose();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  if (!request) return null;

  const journalist = request.journalist;
  const fullName = journalist
    ? `${journalist.first_name} ${journalist.last_name}`
    : "";

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[480px] overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle>Review Accreditation Request</SheetTitle>
        </SheetHeader>

        {/* Journalist summary */}
        <div className="bg-muted/40 rounded-lg p-4 mb-6">
          <p className="text-justify font-semibold text-base">{fullName}</p>
          {request.outlet_name && (
            <p className="text-justify text-sm text-muted-foreground">{request.outlet_name}</p>
          )}
          {journalist?.email && (
            <p className="text-justify text-xs text-muted-foreground mt-1">{journalist.email}</p>
          )}
          <p className="text-justify text-xs text-muted-foreground mt-2">
            Submitted:{" "}
            {new Date(request.created_at).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </p>
        </div>

        {/* Document flags */}
        <div className="mb-6">
          <Label className="text-sm font-semibold mb-3 block">
            Document Completeness
          </Label>
          <div className="space-y-3">
            {Object.keys(DOC_LABELS).map((key) => (
              <div key={key} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Label htmlFor={`doc-${key}`} className="cursor-pointer">
                    {DOC_LABELS[key]}
                  </Label>
                  {docUrls[key] && (
                    <button
                      type="button"
                      onClick={() => viewDocument(docUrls[key])}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline shrink-0"
                    >
                      <ExternalLink className="w-3 h-3" /> View
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    ref={(el) => (fileRefs.current[key] = el)}
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileUpload(key, f);
                      e.target.value = "";
                    }}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={uploadingDoc === key}
                    onClick={() => fileRefs.current[key]?.click()}
                  >
                    {uploadingDoc === key ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {docUrls[key] ? "Replace" : "Upload"}
                  </Button>
                  <Switch
                    id={`doc-${key}`}
                    checked={!!docFlags[key]}
                    onCheckedChange={(checked) =>
                      setDocFlags((prev) => ({ ...prev, [key]: checked }))
                    }
                  />
                  <Badge
                    variant="outline"
                    className={
                      docFlags[key]
                        ? "border-green-500 text-green-700"
                        : "border-red-400 text-red-600"
                    }
                  >
                    {docFlags[key] ? "Provided" : "Missing"}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Separator className="mb-6" />

        {/* Accreditation term */}
        <div className="space-y-1.5 mb-4">
          <Label>Accreditation Term</Label>
          <Select value={term} onValueChange={(v) => setTerm(v as AccreditationTerm)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(["short_term", "medium_term", "long_term"] as AccreditationTerm[]).map((t) => (
                <SelectItem key={t} value={t}>
                  {TERM_LABELS[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-justify text-xs text-muted-foreground">{TERM_DESCRIPTIONS[term]}</p>
        </div>

        {/* Status selector */}
        <div className="space-y-1.5 mb-4">
          <Label>Status</Label>
          <Select
            value={status}
            onValueChange={(v) => setStatus(v as RequestStatus)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Reviewer notes */}
        <div className="space-y-1.5 mb-4">
          <Label htmlFor="reviewer_notes">Reviewer Notes</Label>
          <Textarea
            id="reviewer_notes"
            value={reviewerNotes}
            onChange={(e) => setReviewerNotes(e.target.value)}
            placeholder="Internal notes (not visible to journalist)…"
            rows={3}
          />
        </div>

        {/* Rejection reason, shown only when rejected */}
        {status === "rejected" && (
          <div className="space-y-1.5 mb-4">
            <Label htmlFor="rejection_reason">
              Rejection Reason <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="rejection_reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Reason for rejection…"
              rows={3}
              className="border-destructive focus-visible:ring-destructive"
            />
          </div>
        )}

        {/* Action buttons */}
        <div className="pt-4 border-t space-y-2">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              disabled={submitting}
              onClick={() => save("docs_requested")}
            >
              Request Documents
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1 border-destructive text-destructive hover:bg-destructive/10"
              disabled={submitting}
              onClick={() => save("rejected")}
            >
              Reject
            </Button>
          </div>
          <Button
            type="button"
            className="w-full text-white hover:opacity-90"
            style={{ backgroundColor: "#006B3C" }}
            disabled={submitting}
            onClick={() => save("approved")}
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Approve
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            disabled={submitting}
            onClick={() => save()}
          >
            Save Changes
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
